/*
 * Google Maps utilities used by RSP
 */
import { createRoot } from 'react-dom/client';
import { getIsTrainIncident } from '@rsos/base-utils';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { ACTIVE_ASSAILANT } from '@rsos/constants/alertTypes';
import { getFlag } from '@rsos/feature-flags';
import {
  isGoogleGeocoderReady,
  MapsNotReadyError,
} from '@rsos/maps/interface/reverseGeocode/googleGeocoderReady';
import safeCall from '@rsos/utils/safeCall';

/**
 * Adapter that wraps google.maps.marker.AdvancedMarkerElement with the same
 * API surface as the deprecated google.maps.Marker,
 *
 * https://developers.google.com/maps/deprecations#google-maps-marker
 */
class AdvancedMarkerAdapter {
  constructor(markerOpts = {}) {
    const { position, map, icon, zIndex, opacity, label, ...customProps } =
      markerOpts;

    Object.assign(this, customProps);

    this._isAdvancedMarkerAdapter = true;
    this._iconUrl = null;
    this._map = map ?? null;
    this._eventListeners = new Map();

    this._container = document.createElement('div');
    this._container.style.display = 'flex';
    this._container.style.flexDirection = 'column';
    this._container.style.alignItems = 'center';
    this._container.style.pointerEvents = 'auto';

    if (opacity !== undefined) {
      this._container.style.opacity = opacity;
    }

    this._imgElement = null;
    this._labelElement = null;

    if (icon) {
      this._setIconInternal(icon);
    }

    if (label) {
      this._setLabelInternal(label);
    }

    const latLngPosition = position
      ? typeof position.lat === 'function'
        ? position
        : new window.google.maps.LatLng(position.lat, position.lng)
      : undefined;

    this._marker = new window.google.maps.marker.AdvancedMarkerElement({
      position: latLngPosition,
      map: map || undefined,
      content: this._container,
      zIndex,
    });
  }

  /** @returns {google.maps.LatLng|null} */
  get position() {
    const pos = this._marker.position;
    if (!pos) return pos;
    if (typeof pos.lat === 'function') return pos;
    return new window.google.maps.LatLng(pos.lat, pos.lng);
  }

  get map() {
    return this._map;
  }

  _setIconInternal(icon) {
    if (!this._imgElement) {
      this._imgElement = document.createElement('img');
      this._imgElement.style.display = 'block';
      this._imgElement.draggable = false;

      if (this._labelElement) {
        this._container.insertBefore(this._imgElement, this._labelElement);
      } else {
        this._container.appendChild(this._imgElement);
      }
    }

    if (typeof icon === 'string') {
      this._iconUrl = icon;
      this._imgElement.src = icon;
    } else if (icon && icon.url) {
      this._iconUrl = icon;
      this._imgElement.src = icon.url;
      if (icon.className) {
        this._imgElement.className = icon.className;
      }
    }
  }

  _setLabelInternal(label) {
    if (!this._labelElement) {
      this._labelElement = document.createElement('span');
      this._container.appendChild(this._labelElement);
    }

    if (typeof label === 'string') {
      this._labelElement.textContent = label;
    } else if (label && typeof label === 'object') {
      this._labelElement.textContent = label.text || '';
      if (label.fontSize) this._labelElement.style.fontSize = label.fontSize;
      if (label.fontWeight)
        this._labelElement.style.fontWeight = label.fontWeight;
      if (label.fontFamily)
        this._labelElement.style.fontFamily = label.fontFamily;
      if (label.color) this._labelElement.style.color = label.color;
    }
  }

  setIcon(icon) {
    this._setIconInternal(icon);
  }

  getIcon() {
    return this._iconUrl;
  }

  setPosition(latlng) {
    const pos =
      typeof latlng.lat === 'function'
        ? latlng
        : new window.google.maps.LatLng(latlng.lat, latlng.lng);
    this._marker.position = pos;
  }

  setMap(map) {
    this._map = map;
    this._marker.map = map;
  }

  setOpacity(opacity) {
    this._container.style.opacity = opacity;
  }

  setZIndex(zIndex) {
    this._marker.zIndex = zIndex;
  }

  setCursor(cursor) {
    this._container.style.cursor = cursor;
  }

  /**
   * @param {string} eventName  - 'click' | 'mouseover' | 'mouseout' | …
   * @param {Function} handler  - receives a synthetic event with { latLng, domEvent, stop }
   * @returns {{ remove: Function }}
   */
  addListener(eventName, handler) {
    const eventMap = {
      click: 'click',
      mouseover: 'mouseenter',
      mouseout: 'mouseleave',
    };

    const domEvent = eventMap[eventName] || eventName;

    const wrappedHandler = domEvt => {
      const syntheticEvent = {
        latLng: this.position,
        domEvent: domEvt,
        stop: () => domEvt.stopPropagation(),
      };
      handler(syntheticEvent);
    };

    this._container.addEventListener(domEvent, wrappedHandler);

    if (!this._eventListeners.has(eventName)) {
      this._eventListeners.set(eventName, []);
    }
    this._eventListeners.get(eventName).push({
      wrapped: wrappedHandler,
      domEvent,
    });

    return {
      remove: () => {
        this._container.removeEventListener(domEvent, wrappedHandler);
        const list = this._eventListeners.get(eventName);
        if (list) {
          const idx = list.findIndex(l => l.wrapped === wrappedHandler);
          if (idx !== -1) list.splice(idx, 1);
        }
      },
    };
  }

  // -- Listener cleanup (used by clearInstanceListeners) ---------------------

  _clearListeners(eventName) {
    if (this._eventListeners.has(eventName)) {
      this._eventListeners.get(eventName).forEach(({ wrapped, domEvent }) => {
        this._container.removeEventListener(domEvent, wrapped);
      });
      this._eventListeners.set(eventName, []);
    }
  }

  _clearAllListeners() {
    this._eventListeners.forEach((_list, eventName) => {
      this._clearListeners(eventName);
    });
  }
}

/**
 * Creates a marker using google.maps.marker.AdvancedMarkerElement (wrapped in
 * an adapter for API compatibility). Falls back to the deprecated
 * google.maps.Marker when the marker library is not available.
 *
 * @param {Object} markerOpts - options to create the instance with
 */
export const createGoogleMarker = (markerOpts = {}) => {
  if (
    getFlag('google_advanced_markers') &&
    window.google?.maps?.marker?.AdvancedMarkerElement
  ) {
    try {
      return new AdvancedMarkerAdapter(markerOpts);
    } catch {
      // If AdvancedMarkerElement creation fails (e.g., missing mapId,
      // invalid position), fall through to the legacy Marker.
    }
  }
  // Fallback when flag is disabled, marker library is not loaded,
  // or AdvancedMarkerElement construction failed.
  return new window.google.maps.Marker(markerOpts);
};

/**
 * Creates a google.maps.Circle instance
 * @param {Object} markerCircleOpts - options to create the instance with
 */
export const createGoogleMarkerCircle = (markerCircleOpts = {}) =>
  new window.google.maps.Circle(markerCircleOpts);
/**
 * Creates a google.maps.InfoWindow instance
 * @param {Object} infoWindowOpts - options to create the instance with
 */
export const createGoogleInfoWindow = (infoWindowOpts = {}) => {
  const infoWindow = new window.google.maps.InfoWindow(infoWindowOpts);

  const originalOpen = infoWindow.open.bind(infoWindow);
  infoWindow.open = function (mapOrOpts, anchor) {
    const unwrappedAnchor = anchor?._marker || anchor;
    return originalOpen(mapOrOpts, unwrappedAnchor);
  };

  return infoWindow;
};

/**
 * Remove all event handlers from a Google Maps object instance.
 * Handles both legacy google.maps objects and AdvancedMarkerAdapter instances.
 *
 * Docs: https://developers.google.com/maps/documentation/javascript/events
 * @param {google.maps.Object|AdvancedMarkerAdapter} instance
 * @param {?String=} eventName - One of ['click', 'mouseover', 'mouseout'] event names
 */
export const clearInstanceListeners = (instance, eventName) => {
  try {
    if (instance?._isAdvancedMarkerAdapter) {
      if (eventName) {
        instance._clearListeners(eventName);
      } else {
        instance._clearAllListeners();
      }
    } else if (eventName) {
      window.google.maps.event.clearListeners(instance, eventName);
    } else {
      // Clear all instance listeners
      window.google.maps.event.clearInstanceListeners(instance);
    }
  } catch {
    /*
     Intentionally ignoring logging this error since it can potentially
     spam Sentry and adds no real debugging value. Try-catching to be
     safe since this is a 3rd-party API call.
     */
  }
};

/**
 * Renders the given React component into a google.maps.InfoWindow's DOM element.
 * @param {string} id - The ID for the InfoWindow's placeholder element in the DOM
 * @param {google.maps.InfoWindow} infoWindow - An instance of InfoWindow
 * @param {React.Component} component - An instance of a react component
 */
export const renderIntoInfoWindow = safeCall((id, infoWindow, component) => {
  // Make sure to execute once per call of this function. Otherwise, the
  // handlers registered will pile up indefinitely.
  window.google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
    const container = document.getElementById(id);
    const root = createRoot(container);
    root.render(component);
  });
  return infoWindow;
});

/**
 * Renders the given React component into a google.maps.InfoWindow's DOM element.
 * @param {string} id - The ID for the InfoWindow's placeholder element in the DOM
 * @param {google.maps.InfoWindow} infoWindow - An instance of InfoWindow
 * @param {React.Component} component - An instance of a react component
 */
export const renderIntoExistingInfoWindow = safeCall(
  (id, infoWindow, component) => {
    const container = document.getElementById(id);
    const root = createRoot(container);
    root.render(component);
    return infoWindow;
  },
);

/**
 * Reverse geocode with Google Maps API. Returns a Promise with the results
 * @param {Object} latlng object that contains { lat, lng }
 */
export const googleReverseGeocode = latlng => {
  // Typed so callers can tell a not-yet-loaded Geocoder apart from a real
  // failure; a bare Error here gets Sentry-reported as one.
  if (!isGoogleGeocoderReady()) {
    return Promise.reject(new MapsNotReadyError());
  }
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: latlng }, (result, status) => {
      if (status === 'OK') {
        resolve(result);
      } else {
        // Reject with the Geocoder status (e.g. ZERO_RESULTS, OVER_QUERY_LIMIT,
        // REQUEST_DENIED) rather than the null result, so callers and Sentry
        // get a meaningful reason instead of "null".
        reject(
          new Error(`Google reverse geocode failed with status: ${status}`),
        );
      }
    });
  });
};

/**
 * Geocode with Google Maps API. Returns a Promise with the results
 * @param {string} address - The address to be geocoded
 * @param {Object} bounds - Optional, the bounds to geocode within
 */
export const googleGeocode = (address, bounds) => {
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    const config = { address };
    if (bounds) {
      // bounds is an object that has { northeast: { lat, lng }, southwest: { lat, lng } }
      // make it a google.maps.LatLngBounds.
      config.bounds = new window.google.maps.LatLngBounds(
        bounds.southwest,
        bounds.northeast,
      );
    }
    geocoder.geocode(config, (result, status) => {
      if (status === 'OK') {
        resolve(result);
      } else {
        reject(result);
      }
    });
  });
};

export const getIsUnacceptedActiveAssailant = alert => {
  return (
    alert?.emergency_type?.name === ACTIVE_ASSAILANT &&
    alert?.status?.name !== ACCEPTED
  );
};

export const getIsUnacceptedTrainIncident = alert => {
  return (
    getIsTrainIncident(alert?.emergency_type?.name, true) &&
    alert?.status?.name !== ACCEPTED
  );
};
